import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip } from '@mui/material';
import { PALETTE, BORDERS, BORDER_COLORS, TINTS } from '../theme';

/**
 * Toggle chip row used by Photo Shaper to pick a shape category
 * (USA States / Countries / Continents / Islands / National Parks / Lakes).
 *
 * Always wraps to multiple lines — no horizontal scrolling on mobile, so the
 * user can see every option at once. Sizing is tuned so two chips fit per
 * row at 375px and the whole control settles into 3 lines.
 */
const CategoryChips = ({ value, onChange, options, sx = {} }) => (
    <Box
        sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 0.6, sm: 0.75 },
            justifyContent: 'center',
            ...sx
        }}
    >
        {options.map((opt) => {
            const active = opt.value === value;
            return (
                <Chip
                    key={opt.key}
                    label={opt.text}
                    onClick={() => onChange(opt.value)}
                    clickable
                    sx={{
                        height: { xs: 30, sm: 36 },
                        // Tight horizontal padding on mobile so 6 chips fit
                        // into 2–3 rows; looser on desktop for a friendlier
                        // click target.
                        px: { xs: 0.5, sm: 1 },
                        fontWeight: 600,
                        fontSize: { xs: '0.82rem', sm: '0.92rem' },
                        backgroundColor: active ? TINTS.primary : PALETTE.surface,
                        color: active ? PALETTE.primaryDark : PALETTE.text,
                        border: active ? `1px solid ${BORDER_COLORS.primary}` : BORDERS.soft,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            backgroundColor: active ? TINTS.primary : TINTS.primaryGlow,
                            borderColor: BORDER_COLORS.primary
                        },
                        // MUI Chip ships an inner span with its own padding;
                        // override it for the tighter mobile chip.
                        '& .MuiChip-label': {
                            px: { xs: 1, sm: 1.25 }
                        }
                    }}
                />
            );
        })}
    </Box>
);

CategoryChips.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.any.isRequired,
            text: PropTypes.string.isRequired,
            value: PropTypes.any.isRequired
        })
    ).isRequired,
    sx: PropTypes.object
};

export default CategoryChips;
